import React from "react";
import PropTypes from "prop-types";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import { withStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";

import { formatRatioPercent, formatUnit } from "../../utils";

const styles = (theme) => ({
  greyFont: {
    color: "#727272",
  },
  blackFont: {
    color: "#000",
  },
  blueFont: {
    color: theme.palette.primary.main,
  },
  alert: {
    padding: theme.spacing(2),
  },
  whiteBG: {
    backgroundColor: "white",
  },
});

@withStyles(styles)
export class AvailabilityStats extends React.Component {
  static propTypes = {
    nb_alerts: PropTypes.number.isRequired,
    nb_alerts_ratio: PropTypes.number.isRequired,
    vol_alerts_ratio: PropTypes.number.isRequired,
    threshold: PropTypes.number.isRequired,
  };

  render() {
    const { nb_alerts, nb_alerts_ratio, vol_alerts_ratio, threshold } =
      this.props;
    const { classes } = this.props;
    return (
      <Typography className={classes.greyFont}>
        <strong className={classes.blackFont}>{nb_alerts} products </strong> (
        <strong className={classes.blackFont}>
          {formatRatioPercent(nb_alerts_ratio)}
        </strong>{" "}
        of total goods,{" "}
        <strong className={classes.blackFont}>
          {formatRatioPercent(vol_alerts_ratio)}
        </strong>{" "}
        of total volume) are below{" "}
        <strong className={classes.blueFont}>
          {formatRatioPercent(threshold, 0)}
        </strong>{" "}
        availability at least in one month.
      </Typography>
    );
  }
}

@withStyles(styles)
export class CapacityStats extends React.Component {
  static propTypes = {
    nb_alerts: PropTypes.number.isRequired,
    nb_alerts_ratio: PropTypes.number.isRequired,
    minThreshold: PropTypes.number.isRequired,
    maxThreshold: PropTypes.number.isRequired,
  };

  render() {
    const { nb_alerts, nb_alerts_ratio, minThreshold, maxThreshold } =
      this.props;
    const { classes } = this.props;
    return (
      <Typography className={classes.greyFont}>
        <strong className={classes.blackFont}>
          {nb_alerts} factories×technologies{" "}
        </strong>{" "}
        (
        <strong className={classes.blackFont}>
          {formatRatioPercent(nb_alerts_ratio)}
        </strong>{" "}
        of total) are at least in one month below{" "}
        <strong className={classes.blueFont}>
          {formatRatioPercent(minThreshold, 0)}
        </strong>{" "}
        or above{" "}
        <strong className={classes.blueFont}>
          {formatRatioPercent(maxThreshold, 0)}
        </strong>{" "}
        capacity utilization.
      </Typography>
    );
  }
}

@withStyles(styles)
export class ProdVsDemandStats extends React.Component {
  static propTypes = {
    nb_alerts: PropTypes.number.isRequired,
    nb_alerts_ratio: PropTypes.number.isRequired,
    minThreshold: PropTypes.number.isRequired,
    maxThreshold: PropTypes.number.isRequired,
  };

  render() {
    const { nb_alerts, nb_alerts_ratio, minThreshold, maxThreshold } =
      this.props;
    const { classes } = this.props;
    return (
      <Typography className={classes.greyFont}>
        <strong className={classes.blackFont}>
          {nb_alerts} factories×technologies{" "}
        </strong>{" "}
        (
        <strong className={classes.blackFont}>
          {formatRatioPercent(nb_alerts_ratio)}
        </strong>{" "}
        of total) are at least in one month below{" "}
        <strong className={classes.blueFont}>
          {formatRatioPercent(minThreshold, 0)}
        </strong>{" "}
        or above{" "}
        <strong className={classes.blueFont}>
          {formatRatioPercent(maxThreshold, 0)}
        </strong>{" "}
        Production Vs. Demand.
      </Typography>
    );
  }
}

@withStyles(styles)
export class StockStats extends React.Component {
  static propTypes = {
    nb_alerts_below: PropTypes.number.isRequired,
    nb_alerts_below_ratio: PropTypes.number.isRequired,
    vol_alerts_below: PropTypes.number.isRequired,
    vol_alerts_below_ratio: PropTypes.number.isRequired,
    nb_alerts_above: PropTypes.number.isRequired,
    nb_alerts_above_ratio: PropTypes.number.isRequired,
    vol_alerts_above: PropTypes.number.isRequired,
    vol_alerts_above_ratio: PropTypes.number.isRequired,
    showMin: PropTypes.bool,
    showMax: PropTypes.bool,
  };

  static defaultProps = {
    showMin: false,
    showMax: false,
  };

  renderMin() {
    const {
      nb_alerts_below,
      nb_alerts_below_ratio,
      vol_alerts_below,
      vol_alerts_below_ratio,
      showMin,
      classes,
    } = this.props;
    if (!showMin) {
      return null;
    }
    return (
      <Typography gutterBottom className={classes.greyFont}>
        <strong className={classes.blackFont}>
          {nb_alerts_below} products×factories{" "}
        </strong>{" "}
        (
        <strong className={classes.blackFont}>
          {formatRatioPercent(nb_alerts_below_ratio)}
        </strong>{" "}
        of total) have stock below minimum at least in one month. Monthly
        impacted volume is{" "}
        <strong className={classes.blueFont}>
          {formatUnit(vol_alerts_below, "t", 0)}
        </strong>{" "}
        (
        <strong className={classes.blueFont}>
          {formatRatioPercent(vol_alerts_below_ratio)}
        </strong>{" "}
        of total).
      </Typography>
    );
  }

  renderMax() {
    const {
      nb_alerts_above,
      nb_alerts_above_ratio,
      vol_alerts_above,
      vol_alerts_above_ratio,
      showMax,
      classes,
    } = this.props;
    if (!showMax) {
      return null;
    }
    return (
      <Typography className={classes.greyFont}>
        <strong className={classes.blackFont}>
          {nb_alerts_above} products×factories{" "}
        </strong>{" "}
        (
        <strong className={classes.blackFont}>
          {formatRatioPercent(nb_alerts_above_ratio)}
        </strong>{" "}
        of total) have stock above maximum at least in one month. Monthly
        impacted volume is{" "}
        <strong className={classes.blueFont}>
          {formatUnit(vol_alerts_above, "t", 0)}
        </strong>{" "}
        (
        <strong className={classes.blueFont}>
          {formatRatioPercent(vol_alerts_above_ratio)}
        </strong>{" "}
        of total).
      </Typography>
    );
  }

  render() {
    return (
      <>
        {this.renderMin()}
        {this.renderMax()}
      </>
    );
  }
}

@withStyles(styles)
export class RequiredActionsList extends React.Component {
  static propTypes = {
    items: PropTypes.array.isRequired,
    title: PropTypes.string,
  };

  static defaultProps = {
    title: null,
  };

  render() {
    const { title, items, classes } = this.props;
    return (
      <Paper className={classes.alert}>
        {title === null ? null : (
          <Typography variant="h6" gutterBottom>
            {title}
          </Typography>
        )}
        <List>
          {items.length === 0 ? (
            <ListItem disabled>Nothing to show</ListItem>
          ) : (
            items.map((item, index) => <ListItem key={index}>{item}</ListItem>)
          )}
        </List>
      </Paper>
    );
  }
}
